import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePickerWithRange } from '@/components/studio/DatePickerWithRange';
import { DateRange } from 'react-day-picker';
import { format, subDays } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Download, RefreshCw } from 'lucide-react';

// Types for log entries
interface LogEntry {
  id: string;
  event_type: string;
  status: 'success' | 'error' | 'warning' | 'info';
  message: string;
  user_id?: string;
  meta?: Record<string, any>;
  timestamp: string;
  user_email?: string; // Joined from profiles
}

// Status badge component
const StatusBadge = ({ status }: { status: string }) => {
  switch (status.toLowerCase()) {
    case 'error':
      return <Badge variant="destructive">{status}</Badge>;
    case 'warning':
      return <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">{status}</Badge>;
    case 'success':
      return <Badge className="bg-green-100 text-green-800 border-green-300">{status}</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

// Event type badge component
const EventTypeBadge = ({ type }: { type: string }) => {
  let colorClass = '';
  
  if (type.includes('stripe')) {
    colorClass = 'bg-violet-100 text-violet-800 border-violet-300';
  } else if (type.includes('auth') || type.includes('login') || type.includes('user')) {
    colorClass = 'bg-blue-100 text-blue-800 border-blue-300';
  } else if (type.includes('email')) {
    colorClass = 'bg-indigo-100 text-indigo-800 border-indigo-300';
  } else if (type.includes('booking')) {
    colorClass = 'bg-teal-100 text-teal-800 border-teal-300';
  } else if (type.includes('error')) {
    colorClass = 'bg-red-100 text-red-800 border-red-300';
  } else if (type.includes('system')) {
    colorClass = 'bg-gray-100 text-gray-800 border-gray-300';
  }
  
  return <Badge variant="outline" className={colorClass}>{type}</Badge>;
};

const AdminLogViewer = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 7),
    to: new Date(),
  });
  
  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  
  // Event types for filter
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  
  // Fetch event types
  useEffect(() => {
    const fetchEventTypes = async () => {
      try {
        // First try using the edge function
        const { data: functionsData, error: functionsError } = await supabase.functions.invoke('get-event-types');
        
        if (!functionsError && functionsData) {
          setEventTypes(functionsData as string[]);
        } else {
          // Fallback if edge function isn't working
          const { data: logData, error: logError } = await (supabase as any)
            .from('logging_events')
            .select('event_type')
            .order('event_type');
            
          if (logError) throw logError;
          
          if (logData) {
            // Extract unique event types - explicitly cast to string array
            const uniqueTypes = [...new Set(logData.map((log: any) => log.event_type))] as string[];
            setEventTypes(uniqueTypes);
          }
        }
      } catch (err) {
        console.error("Error fetching event types:", err);
      }
    };
    
    fetchEventTypes();
  }, []);
  
  // Fetch logs with filters
  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Use the any type to bypass TypeScript checking
      let query = (supabase as any)
        .from('logging_events')
        .select(`
          id,
          event_type,
          status,
          message,
          user_id,
          meta,
          timestamp,
          profiles:user_id (email)
        `);
        
      query = query.order('timestamp', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);
        
      // Apply filters
      if (eventTypeFilter) {
        query = query.eq('event_type', eventTypeFilter);
      }
      
      if (statusFilter) {
        query = query.eq('status', statusFilter);
      }
      
      if (dateRange?.from) {
        query = query.gte('timestamp', format(dateRange.from, 'yyyy-MM-dd'));
      }
      
      if (dateRange?.to) {
        // Add one day to include the entire end date
        const nextDay = new Date(dateRange.to);
        nextDay.setDate(nextDay.getDate() + 1);
        query = query.lt('timestamp', format(nextDay, 'yyyy-MM-dd'));
      }
      
      if (searchQuery) {
        query = query.or(`message.ilike.%${searchQuery}%,meta.ilike.%${searchQuery}%`);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      if (data) {
        // Transform data to include user email from joined profiles
        const transformedData = data.map((log: any) => ({
          id: log.id,
          event_type: log.event_type,
          status: log.status,
          message: log.message,
          user_id: log.user_id,
          meta: log.meta,
          timestamp: log.timestamp,
          user_email: log.profiles?.email
        })) as LogEntry[];
        
        setLogs(transformedData);
      } else {
        setLogs([]);
      }
    } catch (err: any) {
      console.error("Error fetching logs:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Initial fetch and refetch when filters change
  useEffect(() => {
    fetchLogs();
  }, [page, pageSize, eventTypeFilter, statusFilter, dateRange]);
  
  // Handle search when Enter is pressed
  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      fetchLogs();
    }
  };
  
  // Export logs as CSV
  const exportLogsAsCsv = () => {
    // Convert logs to CSV
    const headers = "ID,Event Type,Status,Message,User ID,User Email,Timestamp,Metadata\n";
    const csvRows = logs.map(log => {
      const meta = log.meta ? JSON.stringify(log.meta).replace(/"/g, '""') : '';
      return `"${log.id}","${log.event_type}","${log.status}","${log.message.replace(/"/g, '""')}","${log.user_id || ''}","${log.user_email || ''}","${log.timestamp}","${meta}"`;
    });
    
    const csvContent = headers + csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    
    // Create download link and trigger it
    const link = document.createElement('a');
    link.href = url;
    link.download = `chrona-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          <span>System Logs</span>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={exportLogsAsCsv}
              disabled={logs.length === 0 || loading}
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchLogs}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardTitle>
        <CardDescription>
          View and filter system logs and events
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="flex-1">
            <Input
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearch}
            />
          </div>
          <div className="w-full md:w-[200px]">
            <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Event Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Events</SelectItem>
                {eventTypes.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-full md:w-[200px]">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Statuses</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="info">Info</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-full md:w-auto">
            <DatePickerWithRange
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
            />
          </div>
        </div>
        
        {/* Logs table */}
        {loading ? (
          <div className="flex justify-center p-8">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
            Error loading logs: {error}
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center p-8 text-muted-foreground">
            No logs found for the selected filters
          </div>
        ) : (
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Timestamp</TableHead>
                  <TableHead className="w-[150px]">Event Type</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead className="w-[180px]">User</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="font-mono text-sm">
                      {new Date(log.timestamp).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <EventTypeBadge type={log.event_type} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={log.status} />
                    </TableCell>
                    <TableCell>{log.message}</TableCell>
                    <TableCell className="text-sm">
                      {log.user_email || log.user_id || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-between">
        <div className="text-sm text-muted-foreground">
          Showing {logs.length} logs
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0 || loading}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page + 1)}
            disabled={logs.length < pageSize || loading}
          >
            Next
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default AdminLogViewer;
